import React, { useState } from 'react';
import { XIcon, InfoIcon, DollarSignIcon, AlertCircleIcon } from 'lucide-react';
interface Broker {
  id: string;
  name: string;
  company: string;
  email: string;
  phone: string;
  status: 'active' | 'pending' | 'inactive';
  defaultRate: number;
  rates: {
    [key: string]: number;
  };
}
interface BrokerRatesModalProps {
  broker: Broker;
  truckTypes: string[];
  onClose: () => void;
  onSave: (brokerId: string, rates: {
    [key: string]: number;
  }) => void;
}
export const BrokerRatesModal = ({
  broker,
  truckTypes,
  onClose,
  onSave
}: BrokerRatesModalProps) => {
  const [rates, setRates] = useState<{
    [key: string]: number;
  }>({
    ...broker.rates
  });
  const [defaultRate, setDefaultRate] = useState(broker.defaultRate);
  const [suppressedTypes, setSuppressedTypes] = useState<string[]>([]);
  const handleRateChange = (truckType: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    setRates({
      ...rates,
      [truckType]: numValue
    });
  };
  const handleDefaultRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value) || 0;
    setDefaultRate(value);
  };
  const applyDefaultToAll = () => {
    const newRates = {
      ...rates
    };
    truckTypes.forEach(type => {
      if (!suppressedTypes.includes(type)) {
        newRates[type] = defaultRate;
      }
    });
    setRates(newRates);
  };
  const toggleSuppressType = (truckType: string) => {
    if (suppressedTypes.includes(truckType)) {
      setSuppressedTypes(suppressedTypes.filter(t => t !== truckType));
      // Restore the rate to default when unsuppressing
      setRates({
        ...rates,
        [truckType]: defaultRate
      });
    } else {
      setSuppressedTypes([...suppressedTypes, truckType]);
      // Set rate to 0 when suppressing
      setRates({
        ...rates,
        [truckType]: 0
      });
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(broker.id, rates);
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto" data-id="element-1287">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] flex flex-col" data-id="element-1288">
        {/* Fixed Header */}
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0 bg-white rounded-t-lg" data-id="element-1289">
          <h3 className="text-lg font-medium" data-id="element-1290">
            Edit Rates for {broker.company}
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700" data-id="element-1291">
            <XIcon size={20} data-id="element-1292" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col h-full min-h-0" data-id="element-1293">
          {/* Scrollable Content Area */}
          <div className="p-6 overflow-y-auto flex-grow" data-id="element-1294">
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-4" data-id="element-1295">
              <div data-id="element-1296">
                <h4 className="font-medium" data-id="element-1297">{broker.name}</h4>
                <p className="text-sm text-gray-500" data-id="element-1298">{broker.email}</p>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2" data-id="element-1299">
                <label className="text-sm font-medium whitespace-nowrap" data-id="element-1300">
                  Default Rate:
                </label>
                <div className="flex items-center" data-id="element-1301">
                  <span className="px-3 py-2 bg-gray-100 border border-r-0 border-gray-300 rounded-l-md" data-id="element-1302">
                    $
                  </span>
                  <input type="number" value={defaultRate} onChange={handleDefaultRateChange} className="w-24 border border-gray-300 p-2" min="0" step="0.01" data-id="element-1303" />
                </div>
                <button type="button" onClick={applyDefaultToAll} className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-md text-sm whitespace-nowrap" data-id="element-1304">
                  Apply to All
                </button>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4" data-id="element-1305">
              <div className="flex items-start gap-2" data-id="element-1306">
                <InfoIcon size={18} className="text-blue-500 mt-0.5 flex-shrink-0" data-id="element-1307" />
                <div className="text-sm text-blue-800" data-id="element-1308">
                  <p data-id="element-1309">
                    Set custom rates for each truck type or use the default
                    rate. Check "Suppress" to hide a truck type from this
                    broker.
                  </p>
                </div>
              </div>
            </div>
            <div className="border rounded-md overflow-hidden mb-4" data-id="element-1310">
              <div className="overflow-x-auto" data-id="element-1311">
                <table className="w-full" data-id="element-1312">
                  <thead data-id="element-1313">
                    <tr className="bg-gray-50" data-id="element-1314">
                      <th className="p-3 text-left" data-id="element-1315">Truck Type</th>
                      <th className="p-3 text-left" data-id="element-1316">Rate ($/hour)</th>
                      <th className="p-3 text-left" data-id="element-1317">Suppress</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y" data-id="element-1318">
                    {truckTypes.map(truckType => <tr key={truckType} className={suppressedTypes.includes(truckType) ? 'bg-gray-100' : ''} data-id="element-1319">
                        <td className="p-3" data-id="element-1320">{truckType}</td>
                        <td className="p-3" data-id="element-1321">
                          <div className="flex items-center" data-id="element-1322">
                            <span className="px-3 py-2 bg-gray-100 border border-r-0 border-gray-300 rounded-l-md" data-id="element-1323">
                              $
                            </span>
                            <input type="number" value={rates[truckType] || 0} onChange={e => handleRateChange(truckType, e.target.value)} className="w-24 border border-gray-300 p-2 rounded-r-md" min="0" step="0.01" disabled={suppressedTypes.includes(truckType)} data-id="element-1324" />
                          </div>
                        </td>
                        <td className="p-3" data-id="element-1325">
                          <label className="flex items-center" data-id="element-1326">
                            <input type="checkbox" checked={suppressedTypes.includes(truckType)} onChange={() => toggleSuppressType(truckType)} className="mr-2" data-id="element-1327" />
                            Suppress
                          </label>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
            {suppressedTypes.length > 0 && <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 p-3 rounded-md mb-4" data-id="element-1328">
                <AlertCircleIcon size={18} className="text-amber-500 flex-shrink-0 mt-0.5" data-id="element-1329" />
                <div data-id="element-1330">
                  <p data-id="element-1331">
                    <strong data-id="element-1332">Note:</strong> Suppressed truck types will not be
                    available for this broker when assigning jobs.
                  </p>
                </div>
              </div>}
            <div className="flex items-start gap-2 text-sm text-gray-600 bg-gray-50 p-3 rounded-md" data-id="element-1333">
              <DollarSignIcon size={18} className="text-gray-500 flex-shrink-0 mt-0.5" data-id="element-1334" />
              <div data-id="element-1335">
                <p data-id="element-1336">
                  These rates will be used when calculating job costs. You can
                  update these rates at any time.
                </p>
              </div>
            </div>
          </div>
          {/* Fixed Footer */}
          <div className="border-t px-6 py-4 flex justify-end gap-3 flex-shrink-0 bg-white rounded-b-lg" data-id="element-1337">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1338">
              Cancel
            </button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" data-id="element-1339">
              Save Rates
            </button>
          </div>
        </form>
      </div>
    </div>;
};